const FRAME_BUDGET_MS = 16.7;
const LONG_GAP_MS = 50;
const SEVERE_GAP_MS = 100;
const STALL_GAP_MS = 250;

export function createHostRafMetrics() {
  return {
    running: false,
    startedAtMs: 0,
    lastFrameMs: null,
    frameCount: 0,
    gapsMs: [],
    maxGapMs: 0,
    lastFps: 0,
  };
}

export function percentileValue(values, percentile) {
  if (!Array.isArray(values) || !values.length) {
    return 0;
  }
  const sorted = values.slice().sort((left, right) => left - right);
  return sorted[Math.min(sorted.length - 1, Math.round((sorted.length - 1) * percentile))];
}

export function summarizeHostRafMetrics(metrics) {
  const gapsMs = Array.isArray(metrics?.gapsMs) ? metrics.gapsMs : [];
  return {
    running: metrics?.running === true,
    frameCount: Math.max(0, Number.parseInt(metrics?.frameCount, 10) || 0),
    fps: Number.isFinite(Number(metrics?.lastFps)) ? Number(metrics.lastFps) : 0,
    gapP95Ms: percentileValue(gapsMs, 0.95),
    gapP99Ms: percentileValue(gapsMs, 0.99),
    maxGapMs: Number.isFinite(Number(metrics?.maxGapMs)) ? Number(metrics.maxGapMs) : 0,
    recentGapsMs: gapsMs.slice(-60),
  };
}

export function hostRafCounterSnapshot(summary) {
  return {
    "host.raf_fps": summary.fps,
    "host.raf_frame_gap_p95_ms": summary.gapP95Ms,
    "host.raf_frame_gap_max_ms": summary.maxGapMs,
    "host.raf_frames": summary.frameCount,
  };
}

function numericEntryValue(entry, key) {
  const numeric = Number(entry?.[key]);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function summarizeMainThreadPressure(rafMetrics, sentinel = null) {
  const gapsMs = Array.isArray(rafMetrics?.gapsMs) ? rafMetrics.gapsMs : [];
  const overBudgetMs = gapsMs.map((gapMs) => Math.max(0, Number(gapMs || 0) - FRAME_BUDGET_MS));
  const observedOverBudgetTotalMs = overBudgetMs.reduce((sum, value) => sum + value, 0);
  const gapTotalMs = gapsMs.reduce((sum, value) => sum + Math.max(0, Number(value || 0)), 0);
  const longGapsMs = gapsMs.filter((gapMs) => Number(gapMs || 0) >= LONG_GAP_MS);
  const severeGapsMs = gapsMs.filter((gapMs) => Number(gapMs || 0) >= SEVERE_GAP_MS);
  const stallGapsMs = gapsMs.filter((gapMs) => Number(gapMs || 0) >= STALL_GAP_MS);
  const loafEntries = Array.isArray(sentinel?.loaf) ? sentinel.loaf : [];
  const loafBlockingMs = loafEntries.map((entry) => numericEntryValue(entry, "blockingDuration"));
  const loafDurationMs = loafEntries.map((entry) => numericEntryValue(entry, "duration"));
  const loafBlockingTotalMs = loafBlockingMs.reduce((sum, value) => sum + Math.max(0, value), 0);
  return {
    frameBudgetMs: FRAME_BUDGET_MS,
    longGapThresholdMs: LONG_GAP_MS,
    severeGapThresholdMs: SEVERE_GAP_MS,
    stallGapThresholdMs: STALL_GAP_MS,
    sampleCount: gapsMs.length,
    pressureRatio: gapTotalMs > 0 ? observedOverBudgetTotalMs / gapTotalMs : 0,
    rafOverBudgetTotalMs: observedOverBudgetTotalMs,
    rafOverBudgetP95Ms: percentileValue(overBudgetMs, 0.95),
    rafLongGapCount: longGapsMs.length,
    rafSevereGapCount: severeGapsMs.length,
    rafStallGapCount: stallGapsMs.length,
    rafMaxGapMs: Number.isFinite(Number(rafMetrics?.maxGapMs)) ? Number(rafMetrics.maxGapMs) : 0,
    recentLongGapsMs: longGapsMs.slice(-20),
    loafEntryCount: loafEntries.length,
    loafBlockingTotalMs,
    loafBlockingP95Ms: percentileValue(loafBlockingMs, 0.95),
    loafBlockingMaxMs: Math.max(0, ...loafBlockingMs),
    loafDurationP95Ms: percentileValue(loafDurationMs, 0.95),
    loafDurationMaxMs: Math.max(0, ...loafDurationMs),
  };
}

export function mainThreadPressureCounterSnapshot(pressure) {
  return {
    "host.main_thread.pressure_ratio": pressure.pressureRatio,
    "host.main_thread.raf_gap_over_budget_p95_ms": pressure.rafOverBudgetP95Ms,
    "host.main_thread.raf_gap_over_budget_total_ms": pressure.rafOverBudgetTotalMs,
    "host.main_thread.raf_long_gap_count": pressure.rafLongGapCount,
    "host.main_thread.raf_severe_gap_count": pressure.rafSevereGapCount,
    "host.main_thread.raf_stall_gap_count": pressure.rafStallGapCount,
    "host.main_thread.loaf_entries": pressure.loafEntryCount,
    "host.main_thread.loaf_blocking_total_ms": pressure.loafBlockingTotalMs,
    "host.main_thread.loaf_blocking_p95_ms": pressure.loafBlockingP95Ms,
    "host.main_thread.loaf_blocking_max_ms": pressure.loafBlockingMaxMs,
  };
}
